"use client";
import { motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";
import Title from "@/core/common/Title";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IPackageDetailsData } from "@/app/package/[slug]/interface/IPackageDetails.interface";
import Link from "next/link";

interface PackageDescriptionProps {
  topTours?: IPackageDetailsData[];
}
export default function PackageDescription({
  topTours = [],
}: PackageDescriptionProps) {
  const tours = topTours;
  const [index, setIndex] = useState(0);

  const nextSlide = () => {
    if (tours.length === 0) return;
    if (index < tours.length - 1) setIndex(index + 1);
    else setIndex(0);
  };

  const prevSlide = () => {
    if (tours.length === 0) return;
    if (index > 0) setIndex(index - 1);
    else setIndex(tours.length - 1);
  };

  return (
    <div className="md:my-[9rem]">
      <Title greenText="Top Travel " yellowText=" Destinations" />

      <div className="hidden md:flex flex-row gap-10 relative mt-[17rem] padding-x">
        <div className="my-10 relative flex-1">
          <p
            className="
    typography-large-body-light
    exclusive-text
    line-clamp-5
    -mt-52
  "
            dangerouslySetInnerHTML={{
              __html: tours[index]?.description || "",
            }}
          ></p>

          <Link href={`/package/${tours[index]?.slug || ""}`}>
            <button className="bg-primary-500 text-white rounded-lg px-5 py-2 exclusive-text mt-2 hover:bg-primary-600">
              View Details
            </button>
          </Link>

          <div className="absolute left-0 top-0 z-30 overflow-visible">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${index * 225}px)` }}
            >
              {tours.map((item: IPackageDetailsData, i: number) => (
                <motion.div
                  key={item.id}
                  layout
                  onClick={() => setIndex(i)}
                  animate={
                    i === index
                      ? {
                          width: 452,
                          height: 600,
                          y: -270,
                          scale: 1,
                          x: 0,
                          zIndex: 30,
                        }
                      : {
                          width: 200,
                          height: 260,
                          y: 0,
                          x: -80,
                          scale: 1,
                          zIndex: 30,
                        }
                  }
                  transition={{ type: "spring", stiffness: 100, damping: 20 }}
                  className={`relative rounded-3xl overflow-hidden shadow-[0_0_12px_0_#0000003D] cursor-pointer mr-6`}
                >
                  <Image
                    src={item?.image || "/placeholder.png"}
                    alt="image"
                    height={300}
                    width={250}
                    className="object-cover h-full w-full"
                  />
                  {i === index && (
                    <figcaption>
                      <h3 className="absolute bottom-12 font-semibold left-[40%] typography-h6-regular -translate-x-1/2 text-white px-3 py-1">
                        {tours[index]?.name}
                      </h3>
                      <p className="absolute bottom-5 left-26 font-semibold typography-large-body-regular -translate-x-1/2 text-white px-3 py-1">
                        NPR {tours[index]?.current_price}
                      </p>
                    </figcaption>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
          {tours.length > 1 && (
            <div className="absolute right-4 top-40 flex gap-3 z-30">
              <button
                onClick={prevSlide}
                className="w-10 h-10 flex items-center justify-center bg-white/70 text-black rounded-full shadow-lg hover:bg-white transition"
              >
                ←
              </button>
              <button
                onClick={nextSlide}
                className="w-10 h-10 flex items-center justify-center bg-white/70 text-black rounded-full shadow-lg hover:bg-white transition"
              >
                →
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="md:hidden flex flex-col gap-6 mt-10 px-4">
        <div className="relative w-full overflow-hidden h-64">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateX(-${index * 145}px)`,
            }}
          >
            {tours.map((item: IPackageDetailsData, i: number) => {
              const isActive = i === index;

              return (
                <motion.div
                  key={item.id}
                  layout
                  onClick={() => setIndex(i)}
                  animate={
                    isActive
                      ? {
                          width: 180,
                          height: 240,
                          y: 0,
                          scale: 1,
                          zIndex: 30,
                        }
                      : {
                          width: 140,
                          height: 180,
                          y: 65,
                          scale: 0.8,
                          zIndex: 10,
                        }
                  }
                  transition={{ type: "spring", stiffness: 100, damping: 20 }}
                  className="relative rounded-2xl overflow-hidden cursor-pointer flex-shrink-0 mx-1"
                >
                  <Image
                    src={item?.image || "/placeholder.png"}
                    alt="image"
                    height={300}
                    width={250}
                    className="object-cover h-full w-full"
                  />

                  {isActive && (
                    <figcaption>
                      <h3 className="absolute bottom-12 left-1/2 transform -translate-x-1/2 typography-h6-regular font-semibold text-white">
                        {tours[index]?.title}
                      </h3>

                      <p className="absolute bottom-4 left-1/2 transform -translate-x-1/2 typography-large-body-regular font-semibold text-white">
                        {tours[index]?.current_price}
                      </p>
                    </figcaption>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Arrows */}
          <div className="absolute right-4 top-40 flex gap-3 z-30">
            <button
              onClick={prevSlide}
              className="w-10 h-10 flex items-center justify-center bg-white/70 text-black rounded-full shadow-lg hover:bg-white transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              onClick={nextSlide}
              className="w-10 h-10 flex items-center justify-center bg-white/70 text-black rounded-full shadow-lg hover:bg-white transition"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-col items-start text-left px-2 mt-4">
          <p className="typography-extra-large-body">{tours[index]?.title}</p>

          <p
            className="typography-small-body font-light mt-3 text-grey-800 line-clamp-6"
            dangerouslySetInnerHTML={{
              __html: tours[index]?.description || "",
            }}
          ></p>
          <Link href={`/package/${tours[index]?.slug || ""}`}>
            <div className="w-full py-3 items-center text-center bg-[#64A058] text-white rounded-[8px] cursor-pointer mt-4">
              View Details
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
